/**
 * 2x3 radio card grid for selecting field types.
 * Each card shows an icon + label. Selected state has ring highlight.
 *
 * Plan: 117-personalization-admin-ui, Epic 01
 */

import {
  Type,
  FileText,
  List,
  CheckSquare,
  ToggleLeft,
  Paperclip,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { FieldType } from "../api/customizationAdminTypes";
import type { LucideIcon } from "lucide-react";

interface FieldTypeOption {
  type: FieldType;
  label: string;
  icon: LucideIcon;
}

const FIELD_TYPE_OPTIONS: FieldTypeOption[] = [
  { type: "Text Input", label: "Text Input", icon: Type },
  { type: "Text Area", label: "Text Area", icon: FileText },
  { type: "Single Select", label: "Single Select", icon: List },
  { type: "Multi Select", label: "Multi Select", icon: CheckSquare },
  { type: "Toggle", label: "Toggle", icon: ToggleLeft },
  { type: "File Upload", label: "File Upload", icon: Paperclip },
];

interface FieldTypeRadioCardsProps {
  value: FieldType;
  onChange: (type: FieldType) => void;
}

export function FieldTypeRadioCards({ value, onChange }: FieldTypeRadioCardsProps) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {FIELD_TYPE_OPTIONS.map(({ type, label, icon: Icon }) => (
        <button
          key={type}
          type="button"
          onClick={() => onChange(type)}
          className={cn(
            "flex flex-col items-center gap-1.5 rounded-sm border p-3 text-center transition-colors",
            "hover:bg-accent/50",
            value === type
              ? "border-primary bg-primary/5 ring-2 ring-primary/20"
              : "border-border",
          )}
        >
          <Icon className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs font-medium">{label}</span>
        </button>
      ))}
    </div>
  );
}

/** Get the icon component for a field type */
export function getFieldTypeIcon(fieldType: FieldType): LucideIcon {
  const option = FIELD_TYPE_OPTIONS.find((o) => o.type === fieldType);
  return option?.icon ?? Type;
}
